import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Integration {
  id: string
  name: string
  type: string
  status: string
  provider: string
  description: string
  config: Record<string, any>
  last_sync: string | null
  sync_frequency: string
  enabled: boolean
  created_at: string
}

export default function IntegrationHubPage() {
  const [integrations, setIntegrations] = useState<Integration[]>([])
  const [statistics, setStatistics] = useState<{total: number; active: number; types: Record<string, number>} | null>(null)
  const [selectedIntegration, setSelectedIntegration] = useState<Integration | null>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [intRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/integrations'),
      fetch(API_BASE + '/integrations/statistics')
    ])
    if (intRes.ok) setIntegrations((await intRes.json()).integrations || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const triggerSync = async (id: string) => {
    const res = await fetch(API_BASE + '/integrations/' + id + '/sync', { method: 'POST' })
    if (res.ok) fetchData()
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'connected': return 'bg-green-100 text-green-800'
      case 'disconnected': return 'bg-red-100 text-red-800'
      case 'pending': return 'bg-yellow-100 text-yellow-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'ecommerce': return '🛒'
      case 'marketplace': return '🏪'
      case 'analytics': return '📊'
      case 'shipping': return '📦'
      case 'payment': return '💳'
      default: return '🔗'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8">Integration Hub</h1>

        {statistics && (
          <div className="grid grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-500">Total Integrations</div>
              <div className="text-3xl font-bold">{statistics.total}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-500">Active</div>
              <div className="text-3xl font-bold text-green-600">{statistics.active}</div>
            </div>
            {Object.entries(statistics.types || {}).slice(0, 2).map(([type, count]) => (
              <div key={type} className="bg-white rounded-lg shadow p-6">
                <div className="text-sm text-gray-500 capitalize">{type}</div>
                <div className="text-3xl font-bold">{count}</div>
              </div>
            ))}
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2">
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b">
                <h2 className="text-xl font-semibold">Connected Integrations</h2>
              </div>
              <div className="divide-y">
                {integrations.map(integration => (
                  <div
                    key={integration.id}
                    className="p-4 hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelectedIntegration(integration)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <span className="text-2xl">{getTypeIcon(integration.type)}</span>
                        <div>
                          <div className="font-semibold">{integration.name}</div>
                          <div className="text-sm text-gray-500">{integration.provider}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(integration.status)}`}>
                          {integration.status}
                        </span>
                        <button
                          onClick={(e) => { e.stopPropagation(); triggerSync(integration.id); }}
                          className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          Sync
                        </button>
                      </div>
                    </div>
                    {integration.last_sync && (
                      <div className="mt-2 text-xs text-gray-400">
                        Last sync: {new Date(integration.last_sync).toLocaleString()}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div>
            {selectedIntegration ? (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">{selectedIntegration.name}</h3>
                <div className="space-y-4">
                  <div>
                    <div className="text-sm text-gray-500">Provider</div>
                    <div className="font-medium">{selectedIntegration.provider}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Type</div>
                    <div className="font-medium capitalize">{selectedIntegration.type}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Status</div>
                    <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(selectedIntegration.status)}`}>
                      {selectedIntegration.status}
                    </span>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Sync Frequency</div>
                    <div className="font-medium">{selectedIntegration.sync_frequency}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Description</div>
                    <div className="text-sm">{selectedIntegration.description}</div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">Add New Integration</h3>
                <div className="space-y-3">
                  {['Amazon', 'eBay', 'Shopify', 'WooCommerce', 'Stripe'].map(provider => (
                    <button
                      key={provider}
                      className="w-full px-4 py-2 border rounded-lg hover:bg-gray-50 text-left"
                    >
                      Connect {provider}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}
